import { LocationConfig } from '../../../../../models/assets-data/LocationConfig.interface';


/**
 * Default location configuration used as a fallback if fetching the location assets file fails.
 * 
 * - Provides a list of predefined company locations, each with:
 *   - A unique location name identifier
 *   - A user-friendly location label
 *   - Service and project email contacts
 *   - Physical address (if available)
 * 
 * Ensures the app can still function with location-related features even if dynamic retrieval fails.
 *
 * MIRROR OF src/assets/data/json-app-data/locations-config.json — every field, not
 * just the ones a reader thinks matter. Keep them in step.
 *
 * The printer names below were missing here entirely while the live file had them,
 * and the gap was invisible until it wasn't: on a failed fetch Kent came through
 * as a facility with no Zebra and no copier, so its printing silently dropped to
 * the browser path. A fallback that quietly changes where the paper comes out is
 * worse than no fallback.
 *
 * The empty strings are deliberate and are NOT the same as omitting the field.
 * Both read as "no printer" to `locationHasBackendPrinter`, but an empty string
 * says a human considered this location and it has no printer yet, where an absent
 * key says nobody filled it in — which is exactly the mistake this comment exists
 * to stop repeating.
 *
 * `browserQrCodeOverrideLabel` and `browserQrCodeOverrideSheet` are `null` on
 * every entry, here and in the live file, and that is the intended resting state
 * — not a placeholder anyone needs to fill in. Null means "print QR codes", the
 * same as every other facility, Dymo-only ones included: they are exactly the
 * sites that need to prove out whether a QR code on a Dymo scans well enough to
 * keep. They are written out rather than omitted so the keys are visible to
 * anyone editing the config: `false` is the kill switch, for once there is
 * real-world evidence it is not worth the space on that document at a given
 * facility.
 *
 * TWO KEYS, ONE PER DOCUMENT, because the answer came back different for each:
 * the QR does not scan reliably on the Dymo label stock in use, while the same
 * code on the copier sheet is fine. Set the label one to `false` to drop it
 * there and keep the sheet scannable; set both to `false` if a partly-scannable
 * set of documents is judged worse than none. See
 * Location.browserQrCodeOverrideLabel.
 */
export const DEFAULT_ASSETS_LOCATION: LocationConfig = {
  locations: [
    {
      locationName: "kent",
      locationLabel: "Kent",
      serviceEmails: ["Kent.Receiving@macmiller.com"],
      projectEmails: ["Kent.Receiving@macmiller.com"],
      address: "22023 64th Ave S, Kent, Washington 98032",
      laydownSpaceAvailable: true,
      zebraPrinterName: "ZEBRA_Kent",
      copierPrinterName: "KG597",
      browserQrCodeOverrideLabel: null,
      browserQrCodeOverrideSheet: null
    },
    {
      locationName: "kennewick",
      locationLabel: "Kennewick",
      serviceEmails: ["Kennewick.Receiving@macmiller.com","service@macmiller.com"],
      projectEmails: ["Kennewick.Receiving@macmiller.com"],
      address: "106010 E, Wiser Pkwy Suite B, Kennewick, WA 99338",
      laydownSpaceAvailable: true,
      zebraPrinterName: "",
      copierPrinterName: "",
      browserQrCodeOverrideLabel: null,
      browserQrCodeOverrideSheet: null
    },
    {
      locationName: "portland",
      locationLabel: "Portland",
      serviceEmails: ["Portland.Receiving@macmiller.com"],
      projectEmails: ["Portland.Receiving@macmiller.com"],
      address: "12911 NE Airport Way, Portland OR, 97230",
      laydownSpaceAvailable: false,
      zebraPrinterName: "",
      copierPrinterName: "",
      browserQrCodeOverrideLabel: null,
      browserQrCodeOverrideSheet: null
    },
    {
      locationName: "sntWarehouse",
      locationLabel: "SNT Warehouse",
      serviceEmails: [],
      projectEmails: [],
      address: "",
      laydownSpaceAvailable: false,
      zebraPrinterName: "",
      copierPrinterName: "",
      browserQrCodeOverrideLabel: null,
      browserQrCodeOverrideSheet: null
    },
    {
      locationName: "spokane",
      locationLabel: "Spokane",
      serviceEmails: ["Kennewick.Receiving@macmiller.com","service@macmiller.com"],
      projectEmails: ["Kennewick.Receiving@macmiller.com"],
      address: "",
      laydownSpaceAvailable: false,
      zebraPrinterName: "",
      copierPrinterName: "",
      browserQrCodeOverrideLabel: null,
      browserQrCodeOverrideSheet: null
    },
    {
      locationName: "norfolk",
      locationLabel: "Norfolk",
      serviceEmails: ["Service.Receiving@macmiller.com"],
      projectEmails: ["Norfolk.Receiving@macmiller.com"],
      address: "3701 South Norfolk Street",
      laydownSpaceAvailable: false,
      zebraPrinterName: "",
      copierPrinterName: "",
      browserQrCodeOverrideLabel: null,
      browserQrCodeOverrideSheet: null
    }
  ]
};
  